<template>
  <!-- InterviewSignal Landing Page - Latest Version with Testimonials -->
  <div id="app">
    <div v-if="loading || (currentUser && isInterviewPage)" class="loading-screen">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading InterviewSignal...</p>
      </div>
    </div>

    <div v-else-if="!currentUser && !isPublicRoute" class="app-container">
      <!-- Header for non-authenticated users -->
      <div class="app-header">
        <div class="header-container">
          <div class="brand-section">
            <div class="brand-logo">
              <h1 class="brand-title">
                <span class="interview-part">Interview</span><span class="signal-part">Signal</span>
              </h1>
            </div>
          </div>
          
          <div class="auth-section" v-if="!justSignedOut">
            <el-button
              @click="openSignupModal"
              class="sign-up-button"
            >
              <span>Sign Up</span>
            </el-button>
            <el-button
              type="primary"
              @click="openAuthModal('login')"
              class="sign-in-button"
            >
              <span>Sign In</span>
            </el-button>
          </div>
        </div>
      </div>
      
      <main class="app-main">
        <div class="welcome-layout">
          <div class="welcome-content-left">
            <div class="welcome-info">
              <!-- Show signed-out message when user just signed out -->
              <div v-if="justSignedOut" class="signed-out-message">
                <h2 class="signed-out-title">Thanks for using InterviewSignal!</h2>
                <p class="signed-out-description">
                  Your session has been securely ended and all data cleared. We hope InterviewSignal helped you ace your interviews!
                </p>
                <div class="signed-out-features">
                  <div class="feature-item">
                    <i class="el-icon-check"></i>
                    <span>Session ended securely</span>
                  </div>
                  <div class="feature-item">
                    <i class="el-icon-delete"></i>
                    <span>All data cleared</span>
                  </div>
                  <div class="feature-item">
                    <i class="el-icon-back"></i>
                    <span>Come back anytime</span>
                  </div>
                </div>
              </div>
              
              <!-- Show normal landing content when not signed out -->
              <div v-else>
                <h2 class="welcome-title">Turn Interview Anxiety Into Interview Dominance</h2>
                <p class="welcome-description">
                  Join the small percentage of candidates using AI coaching to land 3x more job offers than those who don't.
                </p>
                <div class="welcome-features">
                  <div class="feature-item">
                    <i class="el-icon-microphone"></i>
                    <span>Practice with AI feedback</span>
                  </div>
                  <div class="feature-item">
                    <i class="el-icon-headset"></i>
                    <span>Get live interview assistance</span>
                  </div>
                  <div class="feature-item">
                    <i class="el-icon-trophy"></i>
                    <span>Track your improvement</span>
                  </div>
                </div>
                
                <div class="legal-notice">
                  <p class="legal-text">
                    By clicking Continue to join or sign in, you agree to InterviewSignal's 
                    <a href="/legal/user-agreement.html" target="_blank" class="legal-link">User Agreement</a>, 
                    <a href="/legal/privacy-policy.html" target="_blank" class="legal-link">Privacy Policy</a>, 
                    <a href="/legal/cookie-policy.html" target="_blank" class="legal-link">Cookie Policy</a>, and 
                    <a href="/legal/terms-of-service.html" target="_blank" class="legal-link">Terms of Service</a>.
                  </p>
                </div>
                
                <div class="join-section">
                  <span class="join-text">New to InterviewSignal? </span>
                  <a href="#" @click.prevent="openSignupModal" class="join-link">Join now</a>
                </div>
              </div>
            </div>
          </div>
          
          <div class="welcome-content-right">
            <div class="welcome-image">
              <img src="./assets/hero-image.png" alt="InterviewSignal" class="hero-image" />
            </div>
          </div>
        </div>
      </main>

      <!-- Below the fold content sections -->
      <div v-if="!justSignedOut" class="below-fold-sections">
        <!-- Success story strip -->
        <div class="content-strip success-strip">
          <div class="strip-container">
            <div class="strip-content">
              <div class="success-badge">SUCCESS STORY</div>
              <h2>"I went from getting rejected everywhere to landing my dream CMO role at a top Italian fashion brand"</h2>
              <blockquote class="success-quote">
                "After 6 months of rejections, I found InterviewSignal. The AI prep helped me practice hundreds of scenarios, and during my final interview rounds, the live coaching was like having an expert whispering the perfect answers. Result: 4 different offers, including CMO at a famous Italian fashion brand managing tens of millions in marketing budget."
              </blockquote>
              <div class="success-details">
                <div class="success-person">
                  <div class="person-info">
                    <cite class="success-author">Nathan L.</cite>
                    <div class="success-role">Chief Marketing Officer at Italian Fashion Brand</div>
                  </div>
                </div>
                <div class="success-stats">
                  <div class="stat">
                    <span class="stat-number">4 offers</span>
                    <span class="stat-label">received in final month</span>
                  </div>
                  <div class="stat">
                    <span class="stat-number">$10M+</span>
                    <span class="stat-label">marketing budget</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Problem/Solution strip -->
        <div class="content-strip problem-strip">
          <div class="strip-container">
            <div class="strip-content">
              <h2>Why 89% of people fail interviews (and how to be in the 11% who succeed)</h2>
              <p class="strip-description">
                Most candidates walk into interviews completely unprepared. They practice a few questions, hope for the best, and wonder why they keep getting rejected. You need a systematic approach.
              </p>
              <div class="problem-solution">
                <div class="problem-side">
                  <h3>❌ What most people do</h3>
                  <ul class="problem-list">
                    <li>Practice a few questions from Google</li>
                    <li>Wing it during the actual interview</li>
                    <li>Get nervous and freeze up</li>
                    <li>Never know what went wrong</li>
                  </ul>
                </div>
                <div class="solution-side">
                  <h3>✅ What successful candidates do</h3>
                  <ul class="solution-list">
                    <li>Practice with AI that gives instant feedback</li>
                    <li>Get live coaching during real interviews</li>
                    <li>Stay calm with real-time guidance</li>
                    <li>Track progress and improve systematically</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Features/Benefits strip -->
        <div class="content-strip features-strip">
          <div class="strip-container">
            <div class="strip-content">
              <h2>The complete interview mastery system</h2>
              <p class="strip-description">
                Everything you need to go from interview anxiety to job offers. No more wondering "what if I had said something different?"
              </p>
              <div class="features-grid">
                <div class="feature-card">
                  <div class="feature-icon">🎯</div>
                  <h3>AI-Powered Practice</h3>
                  <p>Practice with our AI that analyzes your answers in real-time. Get instant feedback on what works and what doesn't, just like having a personal interview coach.</p>
                  <div class="feature-benefit">Result: Walk into any interview fully prepared</div>
                </div>
                <div class="feature-card">
                  <div class="feature-icon">🎧</div>
                  <h3>Live Interview Assistance</h3>
                  <p>During your actual interview, our AI listens and provides silent suggestions that only you can see. Never struggle for words again.</p>
                  <div class="feature-benefit">Result: Stay confident even with unexpected questions</div>
                </div>
                <div class="feature-card">
                  <div class="feature-icon">📈</div>
                  <h3>Performance Tracking</h3>
                  <p>See exactly how you're improving with detailed analytics. Track your confidence scores, response quality, and interview readiness.</p>
                  <div class="feature-benefit">Result: Know exactly when you're ready to interview</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

            <!-- Authentication Modal -->
      <AuthModal 
        :visible="showAuthModal" 
        :initial-mode="authModalMode"
        @auth-success="handleAuthSuccess"
        @close="showAuthModal = false"
        @open-signup="handleOpenSignup"
      />

      <!-- Signup with Subscription Modal -->
      <SignupWithSubscriptionModal 
        :visible="showSignupModal" 
        @auth-success="handleAuthSuccess"
        @close="showSignupModal = false"
        @close-signup="showSignupModal = false"
        @open-login="handleOpenLogin"
      />
    </div>

    <!-- Public Routes (when not logged in) -->
    <div v-else-if="!currentUser && isPublicRoute" class="public-route-container">
      <router-view/>
    </div>

    <!-- Authenticated User Interface -->
    <div v-else-if="currentUser && !isInterviewPage" class="app-container">
      <!-- Header for authenticated users -->
      <div class="app-header">
        <div class="header-container">
          <div class="brand-section">
            <div class="brand-logo">
              <h1 class="brand-title">
                <span class="interview-part">Interview</span><span class="signal-part">Signal</span>
              </h1>
            </div>
          </div>
          
          <!-- Desktop Navigation -->
          <nav class="navigation desktop-nav" v-if="!isDashboardPage">
            <router-link 
              to="/dashboard" 
              class="nav-link"
              :class="{ active: $router.currentRoute.path === '/dashboard' }"
            >
              <div class="nav-icon">
                <i class="el-icon-data-board"></i>
              </div>
              <span class="nav-text">Dashboard</span>
            </router-link>
            
            <!-- Interview functionality only available in desktop app -->
            <!-- Web users are automatically redirected to dashboard -->
            
            <router-link 
              v-if="isAdmin"
              to="/setting" 
              class="nav-link"
              :class="{ active: $router.currentRoute.path === '/setting' }"
            >
              <div class="nav-icon">
                <i class="el-icon-setting"></i>
              </div>
              <span class="nav-text">Settings</span>
            </router-link>
            
            <router-link 
              v-if="isAdmin"
              to="/admin" 
              class="nav-link"
              :class="{ active: $router.currentRoute.path === '/admin' }"
            >
              <div class="nav-icon">
                <i class="el-icon-key"></i>
              </div>
              <span class="nav-text">Admin</span>
            </router-link>
          </nav>
          
          <div class="auth-section">
            <div class="user-info-display">
              <div class="user-avatar">
                <span class="avatar-text">{{ userInitials }}</span>
              </div>
              <div class="user-details">
                <div class="user-name">{{ displayName }}</div>
                <div class="user-role">{{ currentUser.email || 'User' }}</div>
              </div>
              <el-button 
                type="text" 
                @click="handleSignOut"
                class="sign-out-button"
              >
                <i class="el-icon-switch-button"></i>
                Sign Out
              </el-button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Main Content -->
      <main class="app-main">
        <router-view/>
      </main>

      <!-- Mobile Bottom Navigation -->
      <nav class="mobile-bottom-nav" v-if="!isDashboardPage">
        <div class="mobile-nav-grid">
          <router-link 
            to="/dashboard" 
            class="mobile-nav-item"
            :class="{ active: $router.currentRoute.path === '/dashboard' }"
          >
            <i class="el-icon-data-board"></i>
            <span>Dashboard</span>
          </router-link>
          
          <router-link 
            to="/customize" 
            class="mobile-nav-item"
            :class="{ active: $router.currentRoute.path === '/customize' }"
          >
            <i class="el-icon-user"></i>
            <span>Profile</span>
          </router-link>
          
          <router-link 
            to="/job" 
            class="mobile-nav-item"
            :class="{ active: $router.currentRoute.path === '/job' }"
          >
            <i class="el-icon-suitcase"></i>
            <span>Job</span>
          </router-link>
          
          <router-link 
            to="/ai-prep" 
            class="mobile-nav-item"
            :class="{ active: $router.currentRoute.path === '/ai-prep' }"
          >
            <i class="el-icon-microphone"></i>
            <span>AI Prep</span>
          </router-link>
          
          <!-- Settings/More dropdown for mobile -->
          <div 
            class="mobile-nav-item"
            @click="toggleMobileMenu"
            :class="{ active: showMobileMenu }"
          >
            <i class="el-icon-more"></i>
            <span>More</span>
            
            <!-- Mobile dropdown menu -->
            <div v-if="showMobileMenu" class="mobile-menu-dropdown">
              <router-link 
                v-if="isAdmin"
                to="/setting" 
                class="mobile-menu-item"
                @click="closeMobileMenu"
              >
                <i class="el-icon-setting"></i>
                <span>Settings</span>
              </router-link>
              
              <router-link 
                v-if="isAdmin"
                to="/admin" 
                class="mobile-menu-item"
                @click="closeMobileMenu"
              >
                <i class="el-icon-key"></i>
                <span>Admin</span>
              </router-link>
              
              <div class="mobile-menu-item" @click="handleSignOut">
                <i class="el-icon-switch-button"></i>
                <span>Sign Out</span>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>

    <!-- Universal Footer for all pages -->
    <footer v-if="!loading" class="app-footer">
      <div class="footer-container">
        <div class="footer-content">
          <div class="footer-brand">
            <span class="footer-interview-part">Interview</span><span class="footer-signal-part">Signal</span>
          </div>
          <div class="footer-copyright">
            <span>© 2025</span>
          </div>
          <div class="footer-links">
            <a href="/legal/user-agreement.html" class="footer-link">User Agreement</a>
            <a href="/legal/privacy-policy.html" class="footer-link">Privacy Policy</a>
            <a href="/legal/cookie-policy.html" class="footer-link">Cookie Policy</a>
            <a href="/legal/terms-of-service.html" class="footer-link">Terms of Service</a>
            <a href="https://help.interviewsignal.com" target="_blank" class="footer-link">Help</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { authHelpers, sessionHelpers, supabase } from '@/utils/supabase'
import AdminService from '@/utils/admin-service'
import userDataService from '@/utils/user-data-service'
import AuthModal from '@/components/AuthModal.vue'
import SignupWithSubscriptionModal from '@/components/SignupWithSubscriptionModal.vue'
import SystemSettings from '@/utils/system-settings'
import notificationService from '@/utils/notification-service'

export default {
  name: 'App',
  components: {
    AuthModal,
    SignupWithSubscriptionModal
  },
  data() {
    return {
      loading: true,
      currentUser: null,
      userStatus: 'approved', // approved, pending, rejected
      adminRole: 'user', // user, admin, super_admin
      showAuthModal: false,
      showSignupModal: false,
      authModalMode: 'login', // 'login' or 'signup'
      showEmailVerificationHelper: false,
      showMobileMenu: false,
      isSigningOut: false, // Flag to prevent modals during sign-out
      justSignedOut: false, // Flag to show signed-out state briefly
      // User database - will be populated from Supabase only
      users: []
    }
  },
  computed: {
    isSupabaseConfigured() {
      // Use authHelpers to check if Supabase is properly configured
      return authHelpers.isConfigured()
    },
    
    isPublicRoute() {
      const publicRoutes = ['/subscription', '/subscription/success', '/subscription/cancel'];
      return publicRoutes.includes(this.$route.path);
    },
    
    isInterviewPage() {
      const interviewPages = ['/', '/customize', '/job', '/ai-prep', '/interview-prep'];
      return interviewPages.includes(this.$route.path);
    },
    
    isAdmin() {
      return this.adminRole && this.adminRole !== 'user'
    },
    
    isDashboardPage() {
      return this.$route.path === '/dashboard'
    },
    
    userInitials() {
      if (!this.currentUser) {
        return 'U'
      }
      
      // Try to get name from customization data first, then fallback to user metadata
      const customizationData = this.getCustomizationFromStorage()
      let name = ''
      
      if (customizationData.personalInfo && customizationData.personalInfo.firstName) {
        name = `${customizationData.personalInfo.firstName} ${customizationData.personalInfo.lastName || ''}`.trim()
      } else {
        name = this.currentUser.user_metadata?.full_name || this.currentUser.email || 'U'
      }
      
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    },
    
    displayName() {
      if (!this.currentUser) {
        return 'User'
      }
      
      // Try to get name from customization data first, then fallback to user metadata
      const customizationData = this.getCustomizationFromStorage()
      
      if (customizationData.personalInfo && customizationData.personalInfo.firstName) {
        return `${customizationData.personalInfo.firstName} ${customizationData.personalInfo.lastName || ''}`.trim()
      }
      
      return this.currentUser.user_metadata?.full_name || this.currentUser.email || 'User'
    }
  },
  async mounted() {
    // Security: Setup global error handling for production
    if (process.env.NODE_ENV === 'production' || process.env.VUE_APP_ENVIRONMENT === 'production') {
      // Handle unhandled promise rejections that might expose database URLs
      window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason
        if (error && error.message) {
          // Check if error message contains sensitive information
          const sensitivePatterns = [
            /https:\/\/[a-z0-9]+\.supabase\.co/i,
            /supabase\.co\/rest\/v1/i,
            /404 \(Not Found\)/i
          ]
          
          if (sensitivePatterns.some(pattern => pattern.test(error.message))) {
            console.warn('Network error detected (details hidden in production)')
            event.preventDefault() // Prevent the error from being logged
            return
          }
        }
      })
      
      // Handle regular errors that might expose sensitive information
      window.addEventListener('error', (event) => {
        if (event.message) {
          const sensitivePatterns = [
            /https:\/\/[a-z0-9]+\.supabase\.co/i,
            /supabase\.co\/rest\/v1/i
          ]
          
          if (sensitivePatterns.some(pattern => pattern.test(event.message))) {
            console.warn('Error detected (details hidden in production)')
            event.preventDefault()
            return
          }
        }
      })
    }
    
    // Security: Disable verbose logging in production
    if (process.env.NODE_ENV === 'production' || process.env.VUE_APP_ENVIRONMENT === 'production') {
      // Override Supabase client logging
      if (window.localStorage) {
        window.localStorage.setItem('supabase.auth.debug', 'false')
      }
      
      // Disable console debugging for sensitive operations
      const originalDebug = console.debug
      console.debug = (...args) => {
        const message = args.join(' ')
        if (!/GoTrueClient|access_token|refresh_token|bearer|jwt/i.test(message)) {
          originalDebug.apply(console, args)
        }
      }
    }
    
    // Initialize notification service
    notificationService.init()
    
    // Initialize system settings for API keys
    SystemSettings.initializeSystemSettings()
    
    // Load users from Supabase only
    await this.loadUsers()
    
    // Set up session monitoring
    this.setupSessionMonitoring();
    
    // Check for existing Supabase session BEFORE ending loading state
    if (this.isSupabaseConfigured) {
      try {
        const user = await authHelpers.getCurrentUser()
        if (user) {
          this.currentUser = user
          this.userStatus = await this.getUserStatus(user)
          this.adminRole = await AdminService.getAdminRole(user)
          
          // Update session with latest user data
          sessionHelpers.updateSession(user);
          
          // If authenticated user is on interview pages, redirect IMMEDIATELY before loading ends
          const interviewPages = ['/', '/customize', '/job', '/ai-prep', '/interview-prep']
          if (interviewPages.includes(this.$route.path)) {
            console.log('🌐 Authenticated user on interview page, redirecting to dashboard before loading ends')
            await this.$router.replace('/dashboard')
          }
        }
      } catch (error) {
        console.error('Error checking auth session:', error)
        sessionHelpers.clearSession();
      }
    } else {
      // Use session management for demo/localStorage mode
      const session = sessionHelpers.getSession();
      if (session) {
        try {
          const user = await authHelpers.getCurrentUser()
          if (user) {
            this.currentUser = user
            this.userStatus = await this.getUserStatus(user)
            this.adminRole = await AdminService.getAdminRole(user)
            
            // Extend session on successful check
            sessionHelpers.extendSession();
            
            // If authenticated user is on interview pages, redirect IMMEDIATELY before loading ends
            const interviewPages = ['/', '/customize', '/job', '/ai-prep', '/interview-prep']
            if (interviewPages.includes(this.$route.path)) {
              console.log('🌐 Authenticated user on interview page, redirecting to dashboard before loading ends')
              await this.$router.replace('/dashboard')
            }
          } else {
            // User data not found, clear session
            sessionHelpers.clearSession();
          }
        } catch (error) {
          console.error('Error loading user from session:', error)
          sessionHelpers.clearSession();
        }
      } else {
        // Clean up any legacy localStorage items
        this.clearLegacyStorage()
      }
    }
    
    // Simulate loading but only after authentication checks and redirects
    await new Promise(resolve => setTimeout(resolve, 500))
    
    this.loading = false
    
    // Add click outside listener for mobile menu
    document.addEventListener('click', this.handleClickOutside)
  },
  
  beforeDestroy() {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    openAuthModal(mode = 'login') {
      // Don't open modals during sign-out process
      if (this.isSigningOut || this.justSignedOut) return
      
      this.authModalMode = mode
      this.showAuthModal = true
    },

    openSignupModal() {
      // Don't open modals during sign-out process
      if (this.isSigningOut || this.justSignedOut) return
      
      this.showSignupModal = true
    },

    handleOpenSignup() {
      // Don't open modals during sign-out process
      if (this.isSigningOut || this.justSignedOut) return
      
      this.showSignupModal = true
    },

    handleOpenLogin() {
      // Don't open modals during sign-out process
      if (this.isSigningOut || this.justSignedOut) return
      
      this.authModalMode = 'login'
      this.showAuthModal = true
    },

    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu
    },

    closeMobileMenu() {
      this.showMobileMenu = false
    },

    handleClickOutside(event) {
      // Check if click is outside mobile menu
      const mobileMenu = event.target.closest('.mobile-nav-item')
      if (!mobileMenu && this.showMobileMenu) {
        this.showMobileMenu = false
      }
    },
    
        
    // Load users from Supabase only
    async loadUsers() {
      if (!this.isSupabaseConfigured) {
        console.debug('Supabase not configured - cannot load users')
        this.users = []
        return
      }

      try {
        // Load users quietly for admin functionality
        const result = await AdminService.getAllUsers()
        
        if (result.success) {
          this.users = result.users
        } else {
          console.debug('No users loaded from auth system:', result.error)
          this.users = []
        }
      } catch (error) {
        console.debug('Error loading users (expected if admin access unavailable):', error.message)
        this.users = []
      }
    },

    async getUserStatus(user) {
      // Check if user is admin
      if (AdminService.isAdmin(user)) {
        return 'approved'
      }
      
      // Check status from Supabase database
      if (supabase) {
        try {
          const result = await AdminService.getUserStatus(user.id)
          
          if (!result.success) {
            console.error('Error fetching user status from database:', result.error)
            // Fallback to approved (auto-approve all new users)
            return user.user_metadata?.status || 'approved'
          }
          
          return result.status || 'approved'
        } catch (error) {
          console.error('Error checking user status:', error)
          return user.user_metadata?.status || 'approved'
        }
      }
      
      // Fallback to approved for mock auth (auto-approve all users)
      return user.user_metadata?.status || 'approved'
    },
    
    async handleAuthSuccess(user) {
      this.currentUser = user
      this.userStatus = await this.getUserStatus(user)
      this.adminRole = await AdminService.getAdminRole(user)
      this.showAuthModal = false
      
      // Create extended session (works for both Supabase and mock auth)
      sessionHelpers.createSession(user);
      
      // Clean up legacy storage - no longer needed with Supabase
      this.clearLegacyStorage();
      
      // Get display name from Supabase data
      const customizationData = await this.getCustomizationFromStorage()
      let displayName = user.email
      
      if (customizationData.personalInfo && customizationData.personalInfo.firstName) {
        displayName = `${customizationData.personalInfo.firstName} ${customizationData.personalInfo.lastName || ''}`.trim()
      } else if (user.user_metadata?.full_name) {
        displayName = user.user_metadata.full_name
      }
      
      notificationService.success(`Welcome, ${displayName}!`)
      if (this.adminRole !== 'user') {
        notificationService.info('Admin privileges activated', 'You have access to administrative features')
      }
      
      // Redirect to dashboard after successful login
      this.$router.push('/dashboard')
    },
    
    async handleSignOut() {
      // Set flag to prevent modals during sign-out
      this.isSigningOut = true
      
      // Explicitly close any open modals
      this.showAuthModal = false
      this.showSignupModal = false
      
      // Sign out from auth provider and clear session
      await authHelpers.signOut()
      
      // Clear all user-specific data from localStorage
      this.clearAllUserData()
      
      this.currentUser = null
      this.userStatus = 'approved'
      this.adminRole = 'user'
      
      // Show signed-out state briefly
      this.justSignedOut = true
      
      // Navigate to home page to ensure clean state
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
      
      notificationService.success('Successfully signed out', 'All your data has been cleared. Come back soon!')
      
      // Clear flags after a brief delay to allow clean transition
      setTimeout(() => {
        this.isSigningOut = false
        this.justSignedOut = false
      }, 3000) // 3 seconds
    },

    async clearAllUserData() {
      // Clear user data from Supabase (but keep auth session)
      if (this.currentUser?.id) {
        console.log('🧹 Clearing user data from Supabase...')
        await userDataService.clearUserData(this.currentUser.id)
        console.log('✅ User data cleared from Supabase')
      }
      
      // Clear only legacy localStorage items (keep auth tokens for session)
      this.clearLegacyStorage()
      
      // Force Vue reactivity update
      this.$forceUpdate()
    },

    clearLegacyStorage() {
      // Only clear legacy localStorage items, not auth tokens
      const legacyKeys = [
        'userProfile',
        'interview_customization',
        'jobInfo',
        'interview_sessions',
        'interview_analyses',
        'currentUser' // Legacy user storage
      ]
      
      console.log('🧹 Clearing legacy localStorage items...')
      legacyKeys.forEach(key => {
        const value = localStorage.getItem(key)
        if (value) {
          localStorage.removeItem(key)
          console.log(`✅ Cleared legacy item: ${key}`)
        }
      })
    },
    
    async refreshUserStatus() {
      if (this.currentUser) {
        if (this.isSupabaseConfigured) {
          // Refresh from Supabase
          this.userStatus = await this.getUserStatus(this.currentUser)
        } else {
          // Fallback to mock data
          const updatedUser = this.users.find(u => u.id === this.currentUser.id)
          if (updatedUser) {
            this.currentUser = updatedUser
            this.userStatus = updatedUser.status
          } else {
            this.handleSignOut()
            notificationService.warning('Account Removed', 'Your account has been removed by an administrator')
          }
        }
      }
    },
    
    // Method to be called by child components to get/update users
    getUsers() {
      return this.users
    },
    
    async updateUser(userId, updates) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Updating user:', userId)
      }
      
      // Properly await the admin check
      const isCurrentUserAdmin = await AdminService.isAdmin(this.currentUser)
      
      const userIndex = this.users.findIndex(u => u.id === userId)
      if (userIndex !== -1) {
        // Check if this is a mock user (mock users have IDs like 'user-001', 'user-002', etc.)
        const isMockUser = userId.startsWith('user-')
        
        // Sync with Supabase if configured and user is admin and NOT a mock user
        if (supabase && isCurrentUserAdmin && !isMockUser) {
          try {
            // Update user_profiles table using admin service
            if (updates.status) {
              const result = await AdminService.updateUserStatus(userId, updates.status)
              
              if (!result.success) {
                console.error('Error updating user status in Supabase:', result.error)
                notificationService.error('Database Error', `Failed to update user status in database: ${result.error}`)
                return null // Return null to indicate failure
              } else {
              }
            }
            
            // Update auth user metadata if needed
            if (updates.fullName || updates.role || updates.company) {
              console.log('10. Updating metadata...')
              const metadataUpdates = {}
              if (updates.fullName) metadataUpdates.full_name = updates.fullName
              if (updates.role) metadataUpdates.current_role = updates.role
              if (updates.company) metadataUpdates.company = updates.company
              
              const result = await AdminService.updateUserMetadata(userId, metadataUpdates)
              console.log('11. Metadata update result:', result)
              
              if (!result.success) {
                console.error('Error updating user metadata in Supabase:', result.error)
                notificationService.error('Database Error', `Failed to update user metadata in database: ${result.error}`)
              }
            }
            
            // Only update local state AFTER successful Supabase update
            this.$set(this.users, userIndex, { ...this.users[userIndex], ...updates })
            const updatedUser = this.users[userIndex]
            return updatedUser
            
          } catch (error) {
            console.error('Error syncing user update with Supabase:', error)
            notificationService.error('Database Error', `Failed to update user in database: ${error.message}`)
            return null // Return null to indicate failure
          }
        } else {
          if (isMockUser) {
            console.log('7. Skipping Supabase update - this is a mock user:', userId)
            // For mock users, just update local state
            this.$set(this.users, userIndex, { ...this.users[userIndex], ...updates })
            return this.users[userIndex]
          } else {
            notificationService.error('Permission Error', 'You must be an admin to update users')
            return null
          }
        }
      }
      console.error('User not found:', userId)
      return null
    },
    
    async deleteUser(userId) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Deleting user:', userId)
      }
      
      const userIndex = this.users.findIndex(u => u.id === userId)
      if (userIndex !== -1) {
        // Remove from local state first
        this.users.splice(userIndex, 1)
        
        // Check if this is a mock user (mock users have IDs like 'user-001', 'user-002', etc.)
        const isMockUser = userId.startsWith('user-')
        
        // Sync with Supabase if configured and user is admin and NOT a mock user
        if (supabase && AdminService.isAdmin(this.currentUser) && !isMockUser) {
          try {
            // Delete user from auth using admin service (this will cascade to user_profiles)
            const result = await AdminService.deleteUser(userId)
            
            if (!result.success) {
              console.error('Error deleting user from Supabase:', result.error)
              notificationService.error('Database Error', 'Failed to delete user from database')
            }
          } catch (error) {
            console.error('Error syncing user deletion with Supabase:', error)
          }
        }
        
        return true
      }
      return false
    },
    
    addUser(userData) {
      const newUser = {
        id: 'user-' + Date.now(),
        ...userData,
        createdAt: new Date().toISOString(),
        isAdmin: false,
        emailVerified: true,
        status: 'approved' // Auto-approve all new users
      }
      this.users.push(newUser)
      return newUser
    },
    
    async getCustomizationFromStorage() {
      if (this.currentUser?.id) {
        // Use Supabase for authenticated users
        return await userDataService.getCustomizationData(this.currentUser.id)
      }
      
      // Fallback for non-authenticated users (legacy support)
      return { personalInfo: null }
    },
    
    setupSessionMonitoring() {
      // Check session validity periodically (every 5 minutes)
      setInterval(() => {
        if (this.currentUser && !sessionHelpers.isSessionValid() && !this.isSigningOut) {
          // Session expired, log out user silently
          this.currentUser = null
          this.userStatus = 'approved'
          // Only show auth modal if not in the middle of signing out
          if (!this.isSigningOut && !this.justSignedOut) {
            this.showAuthModal = true
          }
        }
      }, 5 * 60 * 1000) // 5 minutes
      
      // Extend session on user activity
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
      const extendSessionThrottled = this.throttle(() => {
        if (this.currentUser && sessionHelpers.isSessionValid()) {
          sessionHelpers.extendSession()
        }
      }, 60000) // Throttle to once per minute
      
      activityEvents.forEach(event => {
        document.addEventListener(event, extendSessionThrottled, true)
      })
    },
    
    throttle(func, limit) {
      let inThrottle
      return function() {
        const args = arguments
        const context = this
        if (!inThrottle) {
          func.apply(context, args)
          inThrottle = true
          setTimeout(() => inThrottle = false, limit)
        }
      }
    }
  },
  provide() {
    return {
      getUsers: this.getUsers,
      updateUser: this.updateUser,
      deleteUser: this.deleteUser,
      addUser: this.addUser,
      refreshUserStatus: this.refreshUserStatus,
      loadUsers: this.loadUsers
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: #f8fafc;
  min-height: 100vh;
  color: #1e293b;
}

.config-notice {
  background: #fef3c7;
  border-bottom: 1px solid #f59e0b;
  padding: 12px 0;
}

.notice-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 32px;
}

.notice-icon {
  color: #f59e0b;
  font-size: 18px;
}

.notice-text {
  flex: 1;
}

.notice-text strong {
  color: #92400e;
  font-weight: 600;
}

.notice-text p {
  color: #a16207;
  font-size: 14px;
  margin: 2px 0 0 0;
}

.loading-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f8fafc;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #0a66c2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  font-size: 16px;
  color: #64748b;
  font-weight: 500;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.public-route-container {
  min-height: 100vh;
  background: #ffffff;
}

.app-header {
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 80px;
  gap: 24px;
}

.brand-section {
  display: flex;
  align-items: center;
}

.brand-logo {
  display: flex;
  align-items: center;
}

.brand-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  margin: 0;
  display: flex;
  align-items: center;
}

.interview-part {
  background: #0a66c2;
  color: white;
  padding: 6px 8px;
  border-radius: 4px;
  font-weight: 700;
  font-size: inherit;
}

.signal-part {
  color: #0a66c2;
  font-weight: 700;
  font-size: inherit;
  margin-left: 4px;
}

.navigation {
  display: flex;
  gap: 8px;
}

.nav-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 20px;
  text-decoration: none;
  color: #64748b;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 80px;
}

.nav-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.nav-icon i {
  font-size: 18px;
}

.nav-text {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.025em;
}

.nav-link:hover {
  color: #3b82f6;
  background: #f1f5f9;
}

.nav-link:hover .nav-icon {
  background: #dbeafe;
  color: #3b82f6;
}

.nav-link.active {
  color: #3b82f6;
  background: #eff6ff;
}

.nav-link.active .nav-icon {
  background: #dbeafe;
  color: #3b82f6;
}

.auth-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sign-in-button {
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  background: white;
  border: 2px solid #0a66c2;
  color: #0a66c2;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sign-up-button {
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  background: transparent;
  border: none;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.sign-up-button:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.app-main {
  flex: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
  width: 100%;
}

.welcome-layout {
  display: flex;
  align-items: stretch;
  gap: 48px;
  min-height: 60vh;
}

.welcome-content-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.welcome-content-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.welcome-info {
  max-width: 100%;
  width: 100%;
  padding: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.welcome-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.hero-image {
  max-width: 100%;
  max-height: 500px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 10px 25px rgba(0, 0, 0, 0.1));
}

.welcome-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
  text-align: left;
  line-height: 1.2;
  width: 100%;
}

.welcome-description {
  font-size: 17px;
  color: #64748b;
  margin: 0 0 36px 0;
  line-height: 1.65;
  text-align: left;
  max-width: 480px;
}

.welcome-features {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 36px;
  max-width: 400px;
  width: 100%;
}

/* Signed-out message styles */
.signed-out-message {
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
  padding: 48px 0;
}

.signed-out-title {
  font-size: 32px;
  font-weight: 700;
  color: #0a66c2;
  margin: 0 0 20px 0;
  line-height: 1.2;
}

.signed-out-description {
  font-size: 17px;
  color: #64748b;
  margin: 0 0 36px 0;
  line-height: 1.65;
}

.signed-out-features {
  display: flex;
  flex-direction: column;
  gap: 18px;
  align-items: center;
  max-width: 350px;
  margin: 0 auto;
}

.signed-out-features .feature-item {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 16px;
  color: #374151;
  font-weight: 500;
}

.signed-out-features .feature-item i {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 102, 194, 0.1);
  color: #0a66c2;
  border-radius: 8px;
  font-size: 16px;
  flex-shrink: 0;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 16px;
  color: #374151;
  font-weight: 500;
}

.feature-item i {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 102, 194, 0.1);
  color: #0a66c2;
  border-radius: 8px;
  font-size: 16px;
  flex-shrink: 0;
}

.legal-notice {
  margin-bottom: 28px;
  max-width: 500px;
  text-align: left;
}

.legal-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  text-align: left;
}

.legal-link {
  color: #0a66c2;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.legal-link:hover {
  color: #004182;
  text-decoration: underline;
}

.join-section {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 15px;
  justify-content: flex-start;
}

.join-text {
  color: #64748b;
}

.join-link {
  color: #0a66c2;
  text-decoration: none;
  font-weight: 700;
  transition: color 0.2s ease;
}

.join-link:hover {
  color: #004182;
  text-decoration: underline;
}

/* Below the fold sections */
.below-fold-sections {
  background: #f8fafc;
}

.content-strip {
  padding: 80px 0;
}

.success-strip {
  background: #f9fafb;
  color: #1e293b;
  position: relative;
  overflow: hidden;
  transition: background-color 0.3s ease;
}

.success-strip:hover {
  background: #ffffff;
}

.success-badge {
  display: inline-block;
  background: #3b82f6;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}

.success-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  gap: 40px;
  position: relative;
  z-index: 1;
}

.success-person {
  display: block;
  text-align: left;
}

.person-avatar {
  display: none;
}

.person-info {
  text-align: left;
}

.success-author {
  font-size: 20px;
  color: #000000 !important;
  font-style: normal;
  font-weight: 700;
  display: block;
  margin-bottom: 8px;
  text-shadow: 1px 1px 1px rgba(255,255,255,0.8);
}

.success-role {
  font-size: 16px;
  color: #374151 !important;
  font-weight: 500;
}

.success-stats {
  display: flex;
  gap: 32px;
}

.stat {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
}

.problem-strip {
  background: #ffffff;
}

.problem-solution {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-top: 48px;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
}

.problem-side h3,
.solution-side h3 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f5f9;
}

.problem-side h3 {
  color: #374151;
}

.solution-side h3 {
  color: #374151;
}

.problem-list,
.solution-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.problem-list li,
.solution-list li {
  padding: 12px 0;
  font-size: 16px;
  line-height: 1.5;
  border-bottom: 1px solid #f1f5f9;
}

.problem-list li {
  color: #64748b;
}

.solution-list li {
  color: #374151;
  font-weight: 500;
}

.features-strip {
  background: #f9fafb;
  transition: background-color 0.3s ease;
}

.features-strip:hover {
  background: #ffffff;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 48px;
}

.feature-card {
  background: white;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  font-size: 48px;
  margin-bottom: 20px;
  display: block;
}

.feature-card h3 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
}

.feature-card p {
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 20px;
}

.feature-benefit {
  background: #f0f9ff;
  color: #0369a1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border-left: 4px solid #0ea5e9;
}

.strip-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
}

.strip-content {
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
}

.strip-content h2 {
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 32px 0;
  line-height: 1.2;
}

.success-strip h2 {
  color: #1e293b;
}

.prep-strip h2,
.coaching-strip h2 {
  color: #1e293b;
}

.strip-description {
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 48px 0;
  color: #64748b;
}

.success-quote {
  font-size: 24px;
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 24px 0;
  color: #475569;
  font-weight: 400;
}

.success-author {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  font-style: normal;
  font-weight: 500;
}

.strip-features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 48px;
}

.strip-feature {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
}

.strip-feature i {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 102, 194, 0.1);
  color: #0a66c2;
  border-radius: 16px;
  font-size: 28px;
  flex-shrink: 0;
}

.strip-feature-content h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.strip-feature-content p {
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

/* Responsive Design for Below the Fold */
@media (max-width: 768px) {
  .content-strip {
    padding: 60px 0;
  }
  
  .strip-container {
    padding: 0 20px;
  }
  
  .strip-content h2 {
    font-size: 28px;
  }
  
  .success-quote {
    font-size: 20px;
  }
  
  .strip-description {
    font-size: 16px;
  }
  
  .success-details {
    flex-direction: column;
    gap: 24px;
    text-align: center;
  }
  
  .success-stats {
    gap: 24px;
  }
  
  .problem-solution {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  
  .feature-card {
    padding: 24px;
  }
}

.status-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.status-card {
  text-align: center;
  max-width: 500px;
  padding: 48px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
}

.status-card.pending {
  border-left: 4px solid #f59e0b;
}

.status-card.rejected {
  border-left: 4px solid #ef4444;
}

.status-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 32px;
}

.status-card.pending .status-icon {
  background: #fef3c7;
  color: #f59e0b;
}

.status-card.rejected .status-icon {
  background: #fef2f2;
  color: #ef4444;
}

.status-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
}

.status-description {
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.status-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
}

.detail-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}

.detail-item i {
  font-size: 16px;
}

.detail-item:not(.pending) {
  color: #16a34a;
}

.detail-item.pending {
  color: #f59e0b;
}

.sign-out-link {
  color: #64748b;
  font-weight: 500;
}

.sign-out-link:hover {
  color: #3b82f6;
}

.user-info-display {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.user-info-display .user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0a66c2, #004182);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.user-info-display .user-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-info-display .user-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.user-info-display .user-role {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.sign-out-button {
  color: #64748b;
  font-weight: 500;
  padding: 4px 8px;
  font-size: 12px;
}

.sign-out-button:hover {
  color: #ef4444;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Footer Styles */
.app-footer {
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  padding: 16px 0;
  margin-top: auto;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
}

.footer-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
  text-align: center;
  flex-wrap: wrap;
}

.footer-brand {
  margin: 0;
}

.footer-interview-part {
  background: #374151;
  color: white;
  padding: 2px 6px;
  border-radius: 3px 0 0 3px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}

.footer-signal-part {
  background: white;
  color: #374151;
  padding: 2px 6px;
  border-radius: 0 3px 3px 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.footer-brand-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: #374151;
}

.footer-copyright {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  margin: 0;
}

.footer-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  margin: 0;
}

.footer-link {
  font-size: 14px;
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: #374151;
}

/* Responsive Footer */
@media (max-width: 768px) {
  .footer-links {
    gap: 16px;
    flex-direction: column;
  }
  
  .footer-container {
    padding: 0 20px;
  }
  
  .footer-brand-title {
    font-size: 20px;
  }
}

/* Responsive Footer */
@media (max-width: 768px) {
  .footer-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  }
  
  .footer-links {
    gap: 16px;
  }
  
  .footer-container {
    padding: 0 20px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .welcome-layout {
    flex-direction: column;
    gap: 32px;
  }
  
  .welcome-info {
    padding: 32px 20px;
    align-items: center;
    text-align: center;
  }
  
  .welcome-title {
    text-align: center;
  }
  
  .welcome-description {
    text-align: center;
  }
  
  .legal-text {
    text-align: center;
  }
  
  .join-section {
    justify-content: center;
  }
  
  .welcome-title {
    font-size: 28px;
  }
  
  .hero-image {
    max-height: 300px;
  }
  
  .header-container {
    padding: 0 16px;
    height: 70px;
  }
  
  .brand-title {
    font-size: 14px;
  }
  
  .navigation {
    gap: 4px;
  }
  
  .nav-link {
    padding: 8px 12px;
    min-width: 60px;
  }
  
  .nav-text {
    font-size: 11px;
  }
  
  .app-main {
    padding: 16px;
  }
  
  .status-card {
    padding: 32px 24px;
    margin: 0 16px;
  }
}

/* Mobile Bottom Navigation */
.mobile-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 8px 0 16px 0;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.mobile-nav-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  max-width: 500px;
  margin: 0 auto;
  padding: 0 8px;
}

.mobile-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: #6b7280;
  padding: 4px;
  transition: color 0.2s ease;
  font-size: 12px;
}

.mobile-nav-item:hover,
.mobile-nav-item.router-link-active {
  color: #3b82f6;
}

.mobile-nav-item i {
  font-size: 20px;
  margin-bottom: 2px;
}

.mobile-nav-item span {
  font-weight: 500;
  line-height: 1.2;
}

.mobile-menu-dropdown {
  position: absolute;
  bottom: 100%;
  right: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding: 8px 0;
  min-width: 160px;
  margin-bottom: 8px;
}

.mobile-menu-item {
  display: block;
  padding: 12px 16px;
  text-decoration: none;
  color: #374151;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.mobile-menu-item:hover {
  background-color: #f3f4f6;
}

.mobile-menu-item i {
  margin-right: 8px;
  width: 16px;
}

/* Mobile responsive behavior */
@media (max-width: 768px) {
  .desktop-nav {
    display: none;
  }
  
  .mobile-bottom-nav {
    display: block;
  }
  
  .app-main {
    padding-bottom: 80px; /* Add space for bottom nav */
  }
  
  .header-container {
    justify-content: center;
  }
  
  .header-right {
    display: none;
  }
  
  .brand-section {
    flex: 1;
    text-align: center;
  }
}

/* Ensure proper spacing on mobile */
@media (max-width: 480px) {
  .mobile-nav-grid {
    padding: 0 4px;
  }
  
  .mobile-nav-item {
    padding: 2px;
  }
  
  .mobile-nav-item i {
    font-size: 18px;
  }
  
  .mobile-nav-item span {
    font-size: 11px;
  }
}

/* Custom color overrides to match brand theme */
/* Change Element UI success buttons from green to blue */
.el-button--success {
  background-color: #3b82f6 !important;
  border-color: #3b82f6 !important;
  color: #ffffff !important;
}

.el-button--success:hover {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
}

.el-button--success:active {
  background-color: #1d4ed8 !important;
  border-color: #1d4ed8 !important;
}

.el-button--success.is-plain {
  background-color: #eff6ff !important;
  border-color: #3b82f6 !important;
  color: #3b82f6 !important;
}

.el-button--success.is-plain:hover {
  background-color: #dbeafe !important;
  border-color: #2563eb !important;
  color: #2563eb !important;
}

/* Change Element UI danger buttons from red to dark gray/black */
.el-button--danger {
  background-color: #374151 !important;
  border-color: #374151 !important;
  color: #ffffff !important;
}

.el-button--danger:hover {
  background-color: #1f2937 !important;
  border-color: #1f2937 !important;
}

.el-button--danger:active {
  background-color: #111827 !important;
  border-color: #111827 !important;
}

.el-button--danger.is-plain {
  background-color: #f9fafb !important;
  border-color: #374151 !important;
  color: #374151 !important;
}

.el-button--danger.is-plain:hover {
  background-color: #f3f4f6 !important;
  border-color: #1f2937 !important;
  color: #1f2937 !important;
}

/* Override other success/danger elements */
.el-message--success {
  background-color: #eff6ff !important;
  border-color: #3b82f6 !important;
}

.el-message--success .el-message__icon {
  color: #3b82f6 !important;
}

.el-message--error {
  background-color: #f9fafb !important;
  border-color: #374151 !important;
}

.el-message--error .el-message__icon {
  color: #374151 !important;
}

.el-alert--success {
  background-color: #eff6ff !important;
  border-color: #3b82f6 !important;
  color: #1e40af !important;
}

.el-alert--error {
  background-color: #f9fafb !important;
  border-color: #374151 !important;
  color: #374151 !important;
}

/* Override notification colors */
.el-notification--success {
  border-left-color: #3b82f6 !important;
}

.el-notification--error {
  border-left-color: #374151 !important;
}

.el-notification--success .el-notification__icon {
  color: #3b82f6 !important;
}

.el-notification--error .el-notification__icon {
  color: #374151 !important;
}
</style>